const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/**
 * Get short-lived JWT token from localStorage
 * 
 * This returns the JWT token obtained from /api/v1/auth/device endpoint,
 * NOT the initial long-lived device token.
 */
const getAuthToken = () => {
  const token = localStorage.getItem('access_token');
  if (token) {
    // Validate token format (basic check - should be a JWT with 3 parts)
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('[API] Invalid JWT token format - will attempt refresh');
      // Не удаляем device_token - он может быть использован для получения нового JWT
      localStorage.removeItem('access_token');
      localStorage.removeItem('baristaUser');
      return null;
    }
  }
  return token;
};

/**
 * Refresh JWT token using the saved device_token
 * 
 * This function re-exchanges the long-lived device_token for a new short-lived JWT
 * when the current JWT expires. The device_token itself is NOT rotated - it remains
 * the same unless manually rotated by an admin/owner.
 */
const refreshJwtToken = async () => {
  const deviceToken = localStorage.getItem('device_token');

  if (!deviceToken) {
    console.error('[API] No device_token found for JWT refresh');
    return null;
  }

  try {
    console.log('[API] Refreshing JWT token using saved device_token...');
    
    // Simply re-exchange the device_token for a new JWT
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        device_token: deviceToken,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('[API] Failed to refresh JWT:', response.status, errorData);
      return null;
    }

    const data = await response.json();
    const newJwt = data.access_token;

    if (!newJwt) {
      console.error('[API] No access_token in refresh response');
      return null;
    }

    // Store the new JWT
    localStorage.setItem('access_token', newJwt);
    console.log('[API] JWT token refreshed successfully');

    return newJwt;
  } catch (error) {
    console.error('[API] Error during JWT refresh:', error);
    return null;
  }
};

/** Exported for SSE service to refresh token on 401 and reconnect */
export { refreshJwtToken };

/**
 * Make an authenticated API request with automatic token rotation
 */
const apiRequest = async (endpoint, options = {}) => {
  let token = getAuthToken();
  
  if (!token) {
    throw new Error('Токен аутентификации не найден. Пожалуйста, войдите в систему.');
  }

  const url = `${API_BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  let response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  // If we get 401, try to refresh the JWT token and retry the request
  if (response.status === 401) {
    console.log('[API] 401 Unauthorized - Attempting JWT token refresh...');
    
    const newToken = await refreshJwtToken();
    
    if (newToken) {
      // Retry the request with the new token
      console.log('[API] Retrying request with refreshed token...');
      headers['Authorization'] = `Bearer ${newToken}`;
      
      response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      if (response.ok) {
        console.log('[API] Request succeeded after token refresh');
        return response.json();
      }
      
      // Если после обновления JWT запрос все равно не прошел, проверяем причину
      if (response.status === 401) {
        // Новый JWT тоже не принят - device_token действительно невалиден
        const deviceToken = localStorage.getItem('device_token');
        console.error('='.repeat(60));
        console.error('[API] ⚠️ DELETING device_token - New JWT rejected (401)');
        console.error('[API] Device token:', deviceToken ? deviceToken.substring(0, 20) + '...' : 'null');
        console.error('[API] Request URL:', url);
        console.error('='.repeat(60));
        localStorage.removeItem('access_token');
        localStorage.removeItem('baristaUser');
        localStorage.removeItem('device_token');
        localStorage.removeItem('device_id');
        
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
      }
      
      // Другая ошибка (500, 404, etc.) - не удаляем device_token, это проблема сервера
      console.warn('[API] Request failed after token refresh with status:', response.status);
      console.warn('[API] This is likely a server error, not an auth issue');
      // Продолжаем к обработке ошибки ниже (строка 140)
    } else {
      // Не удалось обновить JWT - device_token невалиден
      const deviceToken = localStorage.getItem('device_token');
      console.error('='.repeat(60));
      console.error('[API] ⚠️ DELETING device_token - Failed to refresh JWT');
      console.error('[API] Device token:', deviceToken ? deviceToken.substring(0, 20) + '...' : 'null');
      console.error('[API] This means device_token is invalid or expired');
      console.error('='.repeat(60));
      localStorage.removeItem('access_token');
      localStorage.removeItem('baristaUser');
      localStorage.removeItem('device_token');
      localStorage.removeItem('device_id');
      
      // Only redirect if we're not already on the login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `Ошибка запроса: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Fetch orders for the current device's coffee shop
 * @param {string} dateFrom - Start date (YYYY-MM-DD) - optional
 * @param {string} dateTo - End date (YYYY-MM-DD) - optional
 */
export const fetchOrders = async (dateFrom = null, dateTo = null) => {
  let url = '/api/v1/orders/coffee-shop/current';
  
  // Add date filters if provided
  const params = new URLSearchParams();
  if (dateFrom) {
    params.append('date_from', dateFrom);
  }
  if (dateTo) {
    params.append('date_to', dateTo);
  }
  
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  
  return apiRequest(url);
};

/**
 * Get a specific order by ID
 */
export const getOrder = async (orderId) => {
  return apiRequest(`/api/v1/orders/device/${orderId}`);
};

/**
 * Update order status
 */
export const updateOrderStatus = async (orderId, newStatus, estimatedReadyTime = null) => {
  const body = {
    status: newStatus,
  };
  
  if (estimatedReadyTime) {
    body.estimated_ready_time = estimatedReadyTime;
  }

  return apiRequest(`/api/v1/orders/device/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

/**
 * Confirm an order (change status to 'confirmed')
 */
export const confirmOrder = async (orderId) => {
  return updateOrderStatus(orderId, 'confirmed');
};

/**
 * Reject an order (change status to 'cancelled')
 */
export const rejectOrder = async (orderId) => {
  return updateOrderStatus(orderId, 'cancelled');
};

/**
 * Complete an order (change status to 'ready')
 */
export const completeOrder = async (orderId) => {
  return updateOrderStatus(orderId, 'ready');
};

/**
 * Return an order to awaiting confirmation status
 * @param {number} orderId - Order ID
 * @param {string} reason - Reason for returning (unavailable items description)
 */
export const returnToAwaitingConfirmation = async (orderId, reason = null) => {
  const body = {
    status: 'awaiting_confirmation',
  };
  
  // Add reason/comment if provided
  if (reason) {
    body.comment = reason; // или другое поле, в зависимости от вашего API
  }

  return apiRequest(`/api/v1/orders/device/${orderId}/status`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

// Функция для генерации случайного заказа (для тестирования)
export const generateRandomOrder = () => {
  const names = ['Алексей Кузнецов', 'Ольга Сидорова', 'Дмитрий Волков', 'Елена Морозова', 'Игорь Новиков'];
  const menuItems = [
    'Капучино', 'Латте', 'Эспрессо', 'Американо', 'Флэт-уайт',
    'Круассан', 'Чизкейк', 'Маффин', 'Сэндвич', 'Печенье'
  ];

  const numItems = Math.floor(Math.random() * 3) + 1;
  const items = [];
  
  for (let i = 0; i < numItems; i++) {
    const item = menuItems[Math.floor(Math.random() * menuItems.length)];
    const quantity = Math.floor(Math.random() * 2) + 1;
    items.push({ name: item, quantity });
  }

  const readyInMinutes = Math.floor(Math.random() * 20) + 10; // 10-30 минут

  return {
    id: Date.now(),
    customerName: names[Math.floor(Math.random() * names.length)],
    items,
    readyByTime: new Date(Date.now() + readyInMinutes * 60000).toISOString(),
    status: 'new',
    createdAt: new Date().toISOString()
  };
};

